import React from 'react';
import { MessageCircle, Star, TrendingUp, Shield, Users, BookOpen, Award, Instagram, Youtube, MapPin, ChevronRight, CheckCircle2, Target, LineChart, BarChart3, Brain } from 'lucide-react';

const LandingPage = () => {
  const whatsappLink = "https://wa.me/918758379686?text=Hi%2C%20I%20am%20interested%20in%20your%20share%20market%20classes.%20Please%20share%20details.";
  const instagramLink = "https://www.instagram.com/market.riti/";
  const youtubeLink = "https://www.youtube.com/@MarketRiti";
  const mapsLink = "https://maps.app.goo.gl/YQtyHHazwYeCNmUp6";

  const courses = [
    {
      level: "Beginner Program",
      duration: "5 Hours",
      format: "1-to-1 Online",
      badge: "No Prior Knowledge Required",
      icon: <BookOpen className="w-8 h-8" style={{ color: 'var(--market-green)' }} />,
      description: "For complete beginners who want to understand how the stock market works before risking real money.",
      highlights: [
        "How Indian stock markets function (NSE, BSE, indices)",
        "Understanding shares, futures, and options visually",
        "Basics of candlestick charts and price behavior",
        "Core risk mindset: capital allocation and loss control",
        "Building confidence before trading"
      ],
      outcome: "Gain confidence to read charts, understand market movement, and know what to learn next."
    },
    {
      level: "Intermediate Program",
      duration: "5 Hours",
      format: "1-to-1 Online",
      badge: "Practical Trade Planning",
      icon: <LineChart className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />,
      description: "Focuses on structured decision-making using charts and options concepts.",
      highlights: [
        "Market structure and trend identification",
        "Support, resistance, and price action analysis",
        "Introduction to options and option chain reading",
        "Trade planning with entry, stop-loss, and target",
        "Risk-reward planning and position sizing"
      ],
      outcome: "Learn how to plan and evaluate trades using charts and options logic."
    },
    {
      level: "Advanced Program",
      duration: "5 Hours",
      format: "1-to-1 Online",
      badge: "Options, Systems & AI-Assisted",
      icon: <Brain className="w-8 h-8" style={{ color: 'var(--market-red)' }} />,
      description: "For serious learners who want to build repeatable, rule-based trading systems.",
      highlights: [
        "Advanced options strategies (spreads, option selling)",
        "Risk-defined trading and drawdown control",
        "Converting ideas into rule-based strategies",
        "Using AI tools for market scanning and validation",
        "Designing semi-automated trading workflows"
      ],
      outcome: "Learn how professionals structure options strategies and AI-assisted workflows responsibly."
    }
  ];

  const whyPoints = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "One-to-One Personalised Sessions",
      description: "Individual attention tailored to your learning pace and goals"
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Practical, Chart-Driven Learning",
      description: "Real market examples, not just theory from textbooks"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Strong Risk Management Focus",
      description: "Learn to protect capital before chasing profits"
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "No Tip-Based Trading",
      description: "Build your own decision-making framework, not dependency"
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Professional Credentials",
      description: "Industry-certified instructor with proven expertise"
    }
  ];

  const testimonials = [
    {
      name: "Rajesh Kumar",
      location: "Mumbai",
      text: "The sessions helped me understand markets logically instead of emotionally. Very structured approach with clear explanations."
    },
    {
      name: "Priya Sharma",
      location: "Pune",
      text: "Very structured approach. No hype, just clarity. Finally understood how to read charts and plan trades properly."
    },
    {
      name: "Amit Deshmukh",
      location: "Nagpur",
      text: "Options finally made sense after the intermediate sessions. The one-to-one format really helped clarify my doubts."
    },
    {
      name: "Sneha Patil",
      location: "Bangalore",
      text: "Good balance of discipline, risk management, and practical thinking. Perfect for working professionals like me."
    }
  ];

  return (
    <div className="App" style={{ background: 'var(--bg-primary)' }}>
      {/* Hero Section */}
      <section style={{ position: 'relative', padding: '120px 0 80px', background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            {/* Logo */}
            <div style={{ marginBottom: '40px' }}>
              <img 
                src="/marketriti-logo.png" 
                alt="Marketriti Logo" 
                style={{ 
                  width: '200px', 
                  height: 'auto',
                  margin: '0 auto',
                  display: 'block'
                }} 
              />
            </div>

            <h2 className="display-md" style={{ 
              marginBottom: '24px',
              color: 'var(--text-primary)'
            }}>
              Structured Share Market Coaching.<br />
              From Beginner to AI-Assisted Trading.
            </h2>
            
            <p className="body-lg" style={{ 
              marginBottom: '40px',
              maxWidth: '700px',
              margin: '0 auto 40px'
            }}>
              One-to-one online market education focused on discipline, risk management, and real-world application.
            </p>

            <div style={{ 
              display: 'flex', 
              gap: '16px', 
              justifyContent: 'center',
              flexWrap: 'wrap',
              marginBottom: '60px'
            }}>
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="btn-primary">
                <MessageCircle className="w-5 h-5" />
                Enquire on WhatsApp
              </a>
              <a href="#courses" className="btn-secondary">
                View Courses
                <ChevronRight className="w-5 h-5" />
              </a>
            </div>

            {/* Credentials Badge */}
            <div style={{ 
              display: 'inline-flex',
              alignItems: 'center',
              gap: '16px',
              padding: '16px 24px',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-subtle)',
              borderRadius: '12px'
            }}>
              <Award className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
              <span style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: '500' }}>
                IIM Ahmedabad • IIT Guwahati • 10+ Years Market Experience
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* About Marketriti */}
      <section style={{ padding: '80px 0', background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <h2 className="h1" style={{ marginBottom: '24px', color: 'var(--text-primary)' }}>
              About Marketriti
            </h2>
            <p className="body-lg" style={{ marginBottom: '24px' }}>
              Marketriti is a professional share market education brand focused on one-to-one online coaching for <strong style={{ color: 'var(--text-primary)' }}>Indian equity and derivatives markets</strong>. The training is hosted by <strong style={{ color: 'var(--text-primary)' }}>Mahesh Sangade</strong>, an IIM Ahmedabad and IIT Guwahati alumnus with over a decade of hands-on market experience.
            </p>
            <p className="body-lg" style={{ marginBottom: '32px' }}>
              With professional credentials including CFA Level III and CMT Level I, the approach is practical, non-theoretical, and focused on building real trading discipline. Sessions are conducted in <strong style={{ color: 'var(--text-primary)' }}>Hindi, English, or Marathi</strong> based on participant comfort.
            </p>
            
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '24px',
              marginTop: '40px'
            }}>
              <div style={{ padding: '20px', background: 'var(--bg-tertiary)', borderRadius: '12px', border: '1px solid var(--border-primary)' }}>
                <TrendingUp className="w-8 h-8" style={{ color: 'var(--accent-primary)', margin: '0 auto 12px' }} />
                <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>10+ Years</div>
                <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>Real Market Experience</div>
              </div>
              <div style={{ padding: '20px', background: 'var(--bg-tertiary)', borderRadius: '12px', border: '1px solid var(--border-primary)' }}>
                <Award className="w-8 h-8" style={{ color: 'var(--accent-primary)', margin: '0 auto 12px' }} />
                <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>IIM-A • IIT-G</div>
                <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>Premier Institutions</div>
              </div>
              <div style={{ padding: '20px', background: 'var(--bg-tertiary)', borderRadius: '12px', border: '1px solid var(--border-primary)' }}>
                <Shield className="w-8 h-8" style={{ color: 'var(--accent-primary)', margin: '0 auto 12px' }} />
                <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>CFA L3 • CMT L1</div>
                <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>Professional Certified</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section id="courses" style={{ padding: '80px 0', background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 className="display-md" style={{ marginBottom: '16px', color: 'var(--text-primary)' }}>
              Course Programs
            </h2>
            <p className="body-lg" style={{ maxWidth: '600px', margin: '0 auto' }}>
              Structured learning paths from basics to advanced strategies
            </p>
          </div>

          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '32px',
            marginBottom: '60px'
          }}>
            {courses.map((course, index) => (
              <div key={index} className="feature-card" style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ marginBottom: '24px' }}>
                  {course.icon}
                </div>
                
                <h3 className="h2" style={{ marginBottom: '12px', color: 'var(--text-primary)' }}>
                  {course.level}
                </h3>
                
                <div style={{ 
                  display: 'flex',
                  gap: '12px',
                  marginBottom: '16px',
                  flexWrap: 'wrap'
                }}>
                  <span style={{ 
                    padding: '4px 12px',
                    background: 'var(--accent-bg)',
                    color: 'var(--accent-primary)',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '600'
                  }}>
                    {course.duration}
                  </span>
                  <span style={{ 
                    padding: '4px 12px',
                    background: 'var(--bg-tertiary)',
                    color: 'var(--text-secondary)',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '500'
                  }}>
                    {course.format}
                  </span>
                </div>
                
                <div style={{ 
                  padding: '8px 12px',
                  background: 'var(--bg-tertiary)',
                  borderRadius: '8px',
                  marginBottom: '20px'
                }}>
                  <span style={{ fontSize: '14px', color: 'var(--text-primary)', fontWeight: '500' }}>
                    {course.badge}
                  </span>
                </div>

                <p className="body-md" style={{ marginBottom: '20px' }}>
                  {course.description}
                </p>

                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '12px' }}>
                    What You'll Learn:
                  </h4>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {course.highlights.map((highlight, idx) => (
                      <li key={idx} style={{ 
                        display: 'flex',
                        gap: '8px',
                        marginBottom: '8px',
                        fontSize: '14px',
                        color: 'var(--text-secondary)'
                      }}>
                        <CheckCircle2 className="w-4 h-4" style={{ color: 'var(--accent-primary)', flexShrink: 0, marginTop: '2px' }} />
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div style={{ 
                  padding: '16px',
                  background: 'var(--bg-tertiary)',
                  borderRadius: '8px',
                  marginBottom: '24px'
                }}>
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    <strong style={{ color: 'var(--text-primary)' }}>Outcome:</strong> {course.outcome}
                  </p>
                </div>

                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ marginTop: 'auto' }}>
                  <MessageCircle className="w-5 h-5" />
                  Enquire on WhatsApp
                </a>
              </div>
            ))}
          </div>

          <div style={{ 
            padding: '24px',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '14px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
              ⚠️ All programs are educational in nature. No live trading, stock recommendations, or profit guarantees are provided.
            </p>
          </div>
        </div>
      </section>

      {/* Why Marketriti */}
      <section style={{ padding: '80px 0', background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 className="display-md" style={{ marginBottom: '16px', color: 'var(--text-primary)' }}>
              Why Choose Marketriti
            </h2>
            <p className="body-lg">
              What sets us apart from typical market courses
            </p>
          </div>

          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px'
          }}>
            {whyPoints.map((point, index) => (
              <div key={index} style={{ 
                padding: '32px',
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border-subtle)',
                borderRadius: '16px',
                transition: 'all 0.2s ease'
              }}>
                <div style={{ 
                  width: '48px',
                  height: '48px',
                  background: 'var(--accent-bg)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px',
                  color: 'var(--accent-primary)'
                }}>
                  {point.icon}
                </div>
                <h3 className="h3" style={{ marginBottom: '8px', color: 'var(--text-primary)' }}>
                  {point.title}
                </h3>
                <p className="body-md">
                  {point.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section style={{ padding: '80px 0', background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 className="display-md" style={{ marginBottom: '16px', color: 'var(--text-primary)' }}>
              What Students Say
            </h2>
            <p className="body-lg">
              Feedback from our learners
            </p>
          </div>

          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px'
          }}>
            {testimonials.map((testimonial, index) => (
              <div key={index} style={{ 
                padding: '32px',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-subtle)',
                borderRadius: '16px'
              }}>
                <div style={{ marginBottom: '16px' }}>
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4" style={{ 
                      display: 'inline',
                      color: 'var(--accent-primary)',
                      fill: 'var(--accent-primary)',
                      marginRight: '4px'
                    }} />
                  ))}
                </div>
                <p className="body-md" style={{ marginBottom: '20px' }}>
                  "{testimonial.text}"
                </p>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
                    {testimonial.name}
                  </div>
                  <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
                    {testimonial.location}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Online & Location */}
      <section style={{ padding: '80px 0', background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <h2 className="h1" style={{ marginBottom: '24px', color: 'var(--text-primary)' }}>
              Learn From Anywhere in India
            </h2>
            <p className="body-lg" style={{ marginBottom: '16px' }}>
              1-to-1 Online Sessions Available Across India
            </p>
            <p className="body-md" style={{ marginBottom: '32px' }}>
              <strong style={{ color: 'var(--text-primary)' }}>Specialized in Indian Markets Only</strong> • Sessions conducted in Hindi, English, or Marathi based on your preference
            </p>
            <p className="body-md" style={{ marginBottom: '40px' }}>
              Whether you're in Mumbai, Delhi, Bangalore, or any other city, you can access personalized market education through our online sessions tailored to Indian equity and derivatives markets.
            </p>
            <a href={mapsLink} target="_blank" rel="noopener noreferrer" className="btn-secondary">
              <MapPin className="w-5 h-5" />
              View Location
            </a>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section style={{ padding: '80px 0', background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>
            <h2 className="h1" style={{ marginBottom: '24px', color: 'var(--text-primary)' }}>
              Stay Connected
            </h2>
            <p className="body-lg" style={{ marginBottom: '40px' }}>
              Educational content & market insights shared regularly
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a 
                href={instagramLink} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '16px 32px',
                  background: 'var(--bg-secondary)',
                  border: '2px solid var(--border-primary)',
                  borderRadius: '12px',
                  color: 'var(--text-primary)',
                  textDecoration: 'none',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--accent-primary)';
                  e.currentTarget.style.background = 'var(--accent-bg)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-primary)';
                  e.currentTarget.style.background = 'var(--bg-secondary)';
                }}
              >
                <Instagram className="w-6 h-6" />
                <span style={{ fontWeight: '600' }}>@market.riti</span>
              </a>
              <a 
                href={youtubeLink} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '16px 32px',
                  background: 'var(--bg-secondary)',
                  border: '2px solid var(--border-primary)',
                  borderRadius: '12px',
                  color: 'var(--text-primary)',
                  textDecoration: 'none',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--accent-primary)';
                  e.currentTarget.style.background = 'var(--accent-bg)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-primary)';
                  e.currentTarget.style.background = 'var(--bg-secondary)';
                }}
              >
                <Youtube className="w-6 h-6" />
                <span style={{ fontWeight: '600' }}>MarketRiti</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={{ padding: '80px 0', background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{ 
            maxWidth: '700px',
            margin: '0 auto',
            textAlign: 'center',
            padding: '60px 40px',
            background: 'var(--bg-primary)',
            border: '2px solid var(--accent-primary)',
            borderRadius: '20px'
          }}>
            <h2 className="display-md" style={{ marginBottom: '24px', color: 'var(--text-primary)' }}>
              Serious About Learning Markets the Right Way?
            </h2>
            <p className="body-lg" style={{ marginBottom: '40px' }}>
              Get started with structured, one-to-one market education
            </p>
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ fontSize: '18px', padding: '20px 40px' }}>
              <MessageCircle className="w-6 h-6" />
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ 
        background: 'var(--bg-tertiary)',
        borderTop: '1px solid var(--border-subtle)',
        padding: '60px 24px 40px'
      }}>
        <div className="container">
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '40px',
            marginBottom: '40px'
          }}>
            {/* Brand */}
            <div>
              <img 
                src="/marketriti-logo.png" 
                alt="Marketriti" 
                style={{ 
                  width: '160px', 
                  height: 'auto',
                  marginBottom: '16px'
                }} 
              />
              <p style={{ fontSize: '14px', color: 'var(--text-muted)', lineHeight: '1.6' }}>
                Professional share market education with focus on discipline, risk management, and practical application.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 style={{ 
                fontSize: '16px',
                fontWeight: '600',
                color: 'var(--text-primary)',
                marginBottom: '16px'
              }}>
                Connect
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: 'var(--text-muted)',
                  textDecoration: 'none',
                  fontSize: '14px',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--accent-primary)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
                <a href={instagramLink} target="_blank" rel="noopener noreferrer" style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: 'var(--text-muted)',
                  textDecoration: 'none',
                  fontSize: '14px',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--accent-primary)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
                >
                  <Instagram className="w-4 h-4" />
                  Instagram
                </a>
                <a href={youtubeLink} target="_blank" rel="noopener noreferrer" style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: 'var(--text-muted)',
                  textDecoration: 'none',
                  fontSize: '14px',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--accent-primary)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
                >
                  <Youtube className="w-4 h-4" />
                  YouTube
                </a>
                <a href={mapsLink} target="_blank" rel="noopener noreferrer" style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: 'var(--text-muted)',
                  textDecoration: 'none',
                  fontSize: '14px',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--accent-primary)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
                >
                  <MapPin className="w-4 h-4" />
                  Location
                </a>
              </div>
            </div>

            {/* About */}
            <div>
              <h4 style={{ 
                fontSize: '16px',
                fontWeight: '600',
                color: 'var(--text-primary)',
                marginBottom: '16px'
              }}>
                About
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px', color: 'var(--text-muted)' }}>
                <div>Mahesh Sangade</div>
                <div>IIM Ahmedabad, IIT Guwahati</div>
                <div>CFA L3, CMT L1</div>
                <div>10+ Years Experience</div>
                <div>Hindi • English • Marathi</div>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div style={{ 
            padding: '24px',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)',
            borderRadius: '12px',
            marginBottom: '32px'
          }}>
            <h4 style={{ 
              fontSize: '14px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              marginBottom: '8px'
            }}>
              Disclaimer:
            </h4>
            <p style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: '1.6' }}>
              Marketriti is an educational platform providing training on stock market concepts. We are not registered with the Securities and Exchange Board of India (SEBI) and do not provide investment advisory, stock recommendations, or guaranteed returns. All content is for educational purposes only.
            </p>
          </div>

          {/* Copyright */}
          <div style={{ 
            textAlign: 'center',
            paddingTop: '32px',
            borderTop: '1px solid var(--border-subtle)'
          }}>
            <p style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
              © 2025 Marketriti. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;